import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";

import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";
import { AppState } from "../../reducers";

import * as FuelModesActionTypes from "../../actions/fueling-modes/fueling-modes.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { FuelingModelService } from "src/app/shared/services/fueling-modes/fueling-modes.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
@Injectable()
export class FuelingModeEffects {
  getFuelingModeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.getFuelingModes),
      switchMap((action) =>
        this.fuelingModeService
          .getFuelingModeList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              FuelModesActionTypes.loadFuelingModes({ response: result })
            ),
            catchError((error) => {
              const actions = [
                FuelModesActionTypes.loadFuelingModes({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("FuelingMode.loadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getFuelingModeExcelList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.getFuelingModesExcel),
      switchMap((action) =>
        this.fuelingModeService
          .getFuelingModeList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              FuelModesActionTypes.loadFuelingModesExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                FuelModesActionTypes.loadFuelingModesExcel({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("FuelingMode.loadExcelError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getFuelingModelTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.getFuelingModesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              FuelModesActionTypes.loadFuelingModesTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                FuelModesActionTypes.loadFuelingModesTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("TableConfig.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  saveFuelingModeTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.saveFuelingModesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            FuelModesActionTypes.loadFuelingModesLoadEnd(),
            MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              FuelModesActionTypes.loadFuelingModesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("TableConfig.SaveError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createFuelingMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.createFuelingMode),
      exhaustMap((action) =>
        this.fuelingModeService.createFuelingMode(action).pipe(
          mergeMap((result) => [
            FuelModesActionTypes.resetCreation(),
            FuelModesActionTypes.getFuelingMode({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("FuelingMode.fuelingModeCreated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              FuelModesActionTypes.loadFuelingModesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  error
                    ? this.translate.instant(error)
                    : this.translate.instant(
                        "FuelingMode.fuelingModeCreateError"
                      ),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateFuelingMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.updateFuelingModes),
      exhaustMap((action) =>
        this.fuelingModeService.updateFuelingMode(action.fuelingMode).pipe(
          map((result) => {
            return FuelModesActionTypes.getFuelingMode({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("FuelingMode.fuelingModeUpdated"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              FuelModesActionTypes.loadFuelingModesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  error
                    ? this.translate.instant(error)
                    : this.translate.instant(
                        "FuelingMode.fuelingModeUpdateError"
                      ),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getFuelingMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FuelModesActionTypes.getFuelingMode),
      exhaustMap((action) =>
        this.fuelingModeService.getFuelingMode(action.code).pipe(
          map((result) =>
            FuelModesActionTypes.loadFuelingMode({ response: result })
          ),
          catchError((error) => {
            const actions = [
              FuelModesActionTypes.loadFuelingModesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("FuelingMode.errorLoadingFuelMode"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private translate: TranslateService,
    private fuelingModeService: FuelingModelService,
    private tableConfigService: TableConfigService
  ) {}
}
